// Pure predicates for the single-key capture shortcut (issue 3). The capture
// surfaces are the FIRST keyboard shortcuts in the app, so the guard logic is
// extracted here and unit-tested: a bare `c` must never hijack Cmd/Ctrl-C copy,
// fire while the grower is typing in a field, or fire mid-IME-composition.

// Minimal shape of a KeyboardEvent we depend on (so tests don't need a DOM).
export interface HotkeyEvent {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  isComposing?: boolean;
}

// Minimal shape of the event target we inspect.
export interface MaybeElement {
  tagName?: string;
  isContentEditable?: boolean;
}

/** True when the element is a text-entry control where single keys must type,
 *  not trigger shortcuts (input / textarea / select / contenteditable). */
export function isEditableElement(el: MaybeElement | null | undefined): boolean {
  if (!el) return false;
  const tag = (el.tagName ?? "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return el.isContentEditable === true;
}

/** Whether a bare single-key shortcut should fire at all: no modifier held, not
 *  composing, and focus is not in an editable control. */
export function shouldFireHotkey(e: HotkeyEvent, targetIsEditable: boolean): boolean {
  if (targetIsEditable) return false;
  if (e.metaKey || e.ctrlKey || e.altKey) return false;
  if (e.isComposing) return false;
  return true;
}

/** Whether this event is the configured capture key (default `c`, case-insensitive). */
export function isCaptureKey(e: HotkeyEvent, key = "c"): boolean {
  return e.key.toLowerCase() === key.toLowerCase();
}

/** The decision: should this keydown trigger a pen capture? */
export function shouldCapture(
  e: HotkeyEvent,
  targetIsEditable: boolean,
  key = "c",
): boolean {
  return shouldFireHotkey(e, targetIsEditable) && isCaptureKey(e, key);
}
